<template>
    <div class="relative w-full" ref="wrap">
        <!-- Trigger -->
        <button
            ref="trigger"
            type="button"
            @click="open"
            :disabled="disabled"
            :class="[
                'field-sm', // tinggi, border, rounded, bg, flex
                datepickerClass, // biar bisa override dari luar
                disabled
                    ? 'opacity-50 cursor-not-allowed bg-gray-100'
                    : 'cursor-pointer',
            ]"
        >
            <div class="flex items-center min-w-0 gap-2">
                <!-- text tanggal -->
                <span
                    :class="[
                        'truncate whitespace-nowrap',
                        hasValue ? 'text-gray-800' : 'text-gray-500',
                    ]"
                >
                    {{ displayedText }}
                </span>
            </div>

            <span class="inline-flex items-center ml-2 shrink-0">
                <AppIcon :icon="iconBack" class="w-5 h-5 text-gray-600" />
            </span>
        </button>

        <!-- Popover -->
        <teleport to="body">
            <transition name="fade">
                <div
                    v-if="isOpen"
                    class="fixed z-[9999]"
                    :style="popoverStyle"
                    @click.stop
                >
                    <div
                        class="overflow-hidden bg-white border border-gray-100 shadow-2xl rounded-xl"
                        :style="{ width: `${panelWidth}px` }"
                    >
                        <BaseDate
                            :selectedDate="dateOnlyRange"
                            :range="true"
                            :position="position"
                            :disablePastDates="disablePastDates"
                            :showPrice="showPrice"
                            @dateSelected="onDatePicked"
                        />

                        <!-- Time optional -->
                        <div
                            v-if="enableTime"
                            class="grid grid-cols-2 gap-3 p-4 border-t border-gray-100"
                        >
                            <div class="flex flex-col">
                                <label
                                    class="mb-1 text-xs font-medium text-gray-600"
                                    >Check-in</label
                                >
                                <input
                                    type="time"
                                    :step="minuteStep * 60"
                                    v-model="inTimeStr"
                                    class="h-10 px-3 text-sm border border-gray-300 rounded-lg"
                                />
                            </div>
                            <div class="flex flex-col">
                                <label
                                    class="mb-1 text-xs font-medium text-gray-600"
                                    >Check-out</label
                                >
                                <input
                                    type="time"
                                    :step="minuteStep * 60"
                                    v-model="outTimeStr"
                                    class="h-10 px-3 text-sm border border-gray-300 rounded-lg"
                                />
                            </div>
                        </div>

                        <div
                            class="flex items-center justify-between gap-3 p-3 border-t border-gray-100"
                        >
                            <button
                                class="text-sm text-gray-500 hover:text-gray-700"
                                @click="clearRange"
                            >
                                Clear
                            </button>
                            <div class="flex items-center gap-2">
                                <button
                                    class="px-4 text-sm border rounded-lg h-9"
                                    @click="close"
                                >
                                    Cancel
                                </button>
                                <button
                                    class="px-4 text-sm text-white bg-blue-600 rounded-lg h-9 hover:bg-blue-700 disabled:opacity-50"
                                    :disabled="!validRange"
                                    @click="apply"
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </teleport>
    </div>
</template>

<script>
import BaseDate from "./BaseDate.vue";
import { format } from "date-fns";

export default {
    name: "BaseDateTimeRange",
    components: { BaseDate },
    props: {
        datepickerClass: { type: String, default: "" }, // opsional override
        placeholder: { type: String, default: "Select date" },
        iconBack: { type: String, default: "mdi:calendar" },
        modelValue: {
            type: Object,
            default: () => ({ startDate: null, endDate: null }),
        },
        position: {
            type: String,
            default: "right",
            validator: (v) => ["left", "right"].includes(v),
        },
        disabled: { type: Boolean, default: false },
        disablePastDates: { type: Boolean, default: true },
        showPrice: { type: Boolean, default: false },

        enableTime: { type: Boolean, default: false },
        showTimeInTrigger: { type: Boolean, default: false },
        minuteStep: { type: Number, default: 30 },

        matchTriggerWidth: { type: Boolean, default: true }, // panel ikut lebar field
        maxPanelWidth: { type: Number, default: 420 },
        minPanelWidth: { type: Number, default: 300 },
    },
    data() {
        return {
            isOpen: false,
            popoverStyle: { top: "0px", left: "0px" },
            panelWidth: 340,
            localStart: null,
            localEnd: null,
            inTimeStr: "14:00",
            outTimeStr: "12:00",
        };
    },
    computed: {
        normalized() {
            return {
                startDate: this.safeDate(this.modelValue?.startDate),
                endDate: this.safeDate(this.modelValue?.endDate),
            };
        },
        dateOnlyRange() {
            return {
                startDate: this.normalized.startDate,
                endDate: this.normalized.endDate,
            };
        },
        hasValue() {
            return !!(this.normalized.startDate && this.normalized.endDate);
        },
        displayedText() {
            const s = this.normalized.startDate,
                e = this.normalized.endDate;
            if (!s || !e) return this.placeholder;
            if (this.showTimeInTrigger) {
                return `${format(s, "dd MMM yyyy")} – ${format(
                    e,
                    "dd MMM yyyy"
                )}`;
            }
            return `${format(s, "dd MMM yyyy")} – ${format(e, "dd MMM yyyy")}`;
        },
        validRange() {
            const s = this.localStart,
                e = this.localEnd;
            return s && e && e.getTime() >= s.getTime();
        },
    },
    watch: {
        modelValue: {
            deep: true,
            handler(v) {
                const s = this.safeDate(v?.startDate);
                const e = this.safeDate(v?.endDate);
                if (s) this.inTimeStr = this.toTimeStr(s);
                if (e) this.outTimeStr = this.toTimeStr(e);
            },
        },
        inTimeStr() {
            this.composeLocal();
        },
        outTimeStr() {
            this.composeLocal();
        },
    },
    mounted() {
        document.addEventListener("click", this.onDocClick);
        window.addEventListener("resize", this.reposition, { passive: true });
        window.addEventListener("scroll", this.reposition, { passive: true });
        this.composeLocal();
    },
    beforeUnmount() {
        document.removeEventListener("click", this.onDocClick);
        window.removeEventListener("resize", this.reposition);
        window.removeEventListener("scroll", this.reposition);
    },
    methods: {
        open() {
            if (this.disabled) return;
            this.isOpen = true;
            // seed local
            if (!this.normalized.startDate || !this.normalized.endDate) {
                const today = this.today(),
                    tomorrow = this.tomorrow(today);
                this.localStart = new Date(today);
                this.localEnd = new Date(tomorrow);
            } else {
                this.localStart = new Date(this.normalized.startDate);
                this.localEnd = new Date(this.normalized.endDate);
            }
            this.$nextTick(this.reposition);
        },
        close() {
            this.isOpen = false;
        },
        onDocClick(evt) {
            if (!this.isOpen) return;
            if (this.$refs.wrap?.contains(evt.target)) return;
            this.close();
        },
        reposition() {
            if (!this.isOpen) return;
            const el = this.$refs.trigger;
            if (!el) return;

            // Panel width mengikuti trigger (biar proporsional)
            if (this.matchTriggerWidth) {
                const w = Math.max(
                    this.minPanelWidth,
                    Math.min(el.offsetWidth, this.maxPanelWidth)
                );
                this.panelWidth = w;
            }

            const rect = el.getBoundingClientRect();
            const gap = 8,
                width = this.panelWidth,
                vw = window.innerWidth;

            let left =
                this.position === "right" ? rect.right - width : rect.left;
            left = Math.max(8, Math.min(left, vw - width - 8));
            const top = rect.bottom + gap;

            this.popoverStyle = { top: `${top}px`, left: `${left}px` };
        },
        onDatePicked(range) {
            const sDate = this.safeDate(range?.startDate) || this.today();
            const eDate = this.safeDate(range?.endDate) || this.tomorrow(sDate);
            if (this.enableTime) {
                this.localStart = this.mergeDateTime(sDate, this.inTimeStr);
                this.localEnd = this.mergeDateTime(eDate, this.outTimeStr);
            } else {
                sDate.setHours(0, 0, 0, 0);
                eDate.setHours(0, 0, 0, 0);
                this.localStart = sDate;
                this.localEnd = eDate;
            }
        },
        clearRange() {
            this.localStart = null;
            this.localEnd = null;
            this.$emit("update:modelValue", { startDate: null, endDate: null });
        },
        apply() {
            if (!this.validRange) return;
            this.$emit("update:modelValue", {
                startDate: new Date(this.localStart),
                endDate: new Date(this.localEnd),
            });
            this.close();
        },
        composeLocal() {
            if (!this.enableTime) return;
            if (!this.normalized.startDate || !this.normalized.endDate) return;
            this.localStart = this.mergeDateTime(
                this.normalized.startDate,
                this.inTimeStr
            );
            this.localEnd = this.mergeDateTime(
                this.normalized.endDate,
                this.outTimeStr
            );
        },

        // Helpers
        mergeDateTime(d0, timeStr) {
            const d = new Date(d0);
            const [h, m] = (timeStr || "00:00")
                .split(":")
                .map((n) => parseInt(n || 0, 10));
            d.setHours(h || 0, m || 0, 0, 0);
            return d;
        },
        toTimeStr(d) {
            return `${String(d.getHours()).padStart(2, "0")}:${String(
                d.getMinutes()
            ).padStart(2, "0")}`;
        },
        safeDate(v) {
            if (!v && v !== 0) return null;
            const d = v instanceof Date ? v : new Date(v);
            return Number.isNaN(d.getTime()) ? null : d;
        },
        today() {
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            return d;
        },
        tomorrow(from = null) {
            const b = from ? new Date(from) : this.today();
            const t = new Date(b);
            t.setDate(b.getDate() + 1);
            return t;
        },
    },
};
</script>

<style scoped>
/* Trigger style uniform dengan input lain */
.field-sm {
    @apply w-full h-12 px-4 border border-gray-300 rounded-xl bg-white text-sm flex items-center justify-between min-w-0;
}
.field-sm:focus-visible,
.field-sm:focus {
    outline: none;
}
.field-sm:focus-within {
    @apply ring-2 ring-blue-100 border-blue-500;
}

/* Popover animation */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}
</style>
